import { SegmentGroup } from '@ark-ui/react'
import { useEffect, useState } from 'react'
import { readTheme, writeTheme, type ThemeChoice } from '../theme'

const OPTIONS: { value: ThemeChoice; label: string; hint: string }[] = [
  { value: 'system', label: 'Auto', hint: 'Follow the system setting' },
  { value: 'light', label: 'Light', hint: 'Always light' },
  { value: 'dark', label: 'Dark', hint: 'Always dark' },
]

/**
 * Three states, shown as three, because "system" is a real choice and not the absence of one.
 *
 * A two-state sun/moon toggle cannot express it: once tapped it pins the theme for ever, and a
 * reader whose phone flips to dark at sunset silently stops getting that. So the control is a
 * segmented group and "Auto" is the default it starts on.
 *
 * On Ark's `SegmentGroup` rather than three buttons wired up by hand. What that buys is not the
 * markup — it is the keyboard behaviour a radio group is supposed to have and hand-rolled ones
 * almost never do: arrow keys move between options and the group holds a single tab stop, instead
 * of three buttons each of which has to be tabbed past.
 *
 * The `theme-switching` class exists only for the length of the repaint. Leaving the transition on
 * permanently makes every hover on every card fade, which reads as lag on a page this dense.
 */
export function ThemeToggle() {
  // Mounted state rather than read-at-init: this renders in the jsdom smoke test, where reaching
  // for storage during the first render would throw before anything reaches the DOM.
  const [choice, setChoice] = useState<ThemeChoice>('system')

  useEffect(() => {
    setChoice(readTheme())
  }, [])

  const pick = (next: ThemeChoice) => {
    const root = document.documentElement
    root.classList.add('theme-switching')
    writeTheme(next)
    setChoice(next)
    window.setTimeout(() => root.classList.remove('theme-switching'), 200)
  }

  return (
    <SegmentGroup.Root
      value={choice}
      onValueChange={(details) => details.value && pick(details.value as ThemeChoice)}
      aria-label="Colour theme"
      className="inline-flex overflow-hidden rounded border border-line"
    >
      {OPTIONS.map((option) => (
        <SegmentGroup.Item
          key={option.value}
          value={option.value}
          title={option.hint}
          className="relative inline-flex min-h-8 cursor-pointer items-center justify-center border-l border-line px-2.5 py-1 text-xs leading-none text-ink-faint transition-colors first:border-l-0 hover:bg-surface-raised hover:text-ink has-focus-visible:outline-2 has-focus-visible:outline-offset-[-2px] has-focus-visible:outline-accent data-[state=checked]:bg-accent-bg data-[state=checked]:text-accent"
        >
          <SegmentGroup.ItemText>{option.label}</SegmentGroup.ItemText>
          <SegmentGroup.ItemHiddenInput className="absolute opacity-0" />
        </SegmentGroup.Item>
      ))}
    </SegmentGroup.Root>
  )
}
